import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '@/components/modal/Modal';
import { Col, getItemWithExpiration, Row } from '@/lib/utils';
import { MediumText, RegularText } from '@/lib/Text';
import { getMembershipForMember } from '@/api/memberShip';
import ModalMembershipInfo from '../ModalMembershipInfo';
import { SESSION_REPORT_MEMBERSHIP_INFO_QUERY_KEY } from '@/features/settlementClassification/hooks';

/**
 * 세션 현황 「확인 필요」 회원 상세에서 수강권 하나를 기존 「수강권 정보」 창(ModalMembershipInfo)으로 연다.
 *
 * 세션 현황 응답에는 수강권 id 만 있어서, 수강권 단건 조회(GET v2/memberships/:id — 삭제·종료된 수강권도 읽는다)로
 * 수강권을 받아 창에 넘긴다. 회원 나이·성별은 이 경로에 없으므로 창에서 그 줄을 숨긴다(「미입력」으로 잘못 보이지 않게).
 * 신규/재등록 배지는 수강권의 지금 표시(isNewSales)다 — 정산 기준과 다르면 창 안의 정산 기준 줄이 따로 알린다.
 */
const SessionReportMembershipInfo = ({
    membershipId,
    coachName,
    memberName,
    onClose,
}: {
    membershipId: string;
    coachName: string;
    memberName: string;
    onClose: () => void;
}) => {
    const authorization = getItemWithExpiration('authorization');
    const query = useQuery({
        queryKey: [SESSION_REPORT_MEMBERSHIP_INFO_QUERY_KEY, membershipId],
        queryFn: async () => {
            const response = await getMembershipForMember(membershipId, authorization);
            const membership = response?.data?.membership;
            if (response?.status !== 200 || !membership || Array.isArray(membership) || !membership._id) {
                throw new Error(`MEMBERSHIP_NOT_FOUND_${response?.status ?? 'UNKNOWN'}`);
            }
            return membership;
        },
        // 전역 기본값이 enabled:false 라 반드시 명시한다.
        enabled: !!membershipId && !!authorization,
        retry: false,
        staleTime: 0,
    });

    if (query.data) {
        return (
            <ModalMembershipInfo
                setIsVisible={() => onClose()}
                showMemberDetails={false}
                membershipInfo={{
                    coachName,
                    memberInfo: {
                        memberName,
                        age: undefined as unknown as number,
                        gender: '',
                        isNewSales: query.data.isNewSales === true,
                    } as any,
                    membership: query.data,
                }}
            />
        );
    }

    return (
        <Modal
            isCloseOutside
            isCloseButton
            onClose={onClose}
            setIsVisible={() => onClose()}
            whiteViewWidth={420}
            borderRadius={25}
            modalAlign="center"
        >
            <Col className="w-full px-[32px] pt-[32px] pb-[28px]">
                <MediumText fontSize={20} lineHeight={28} color="#3D4244" className="mb-[8px]">
                    수강권 정보
                </MediumText>
                {query.isError ? (
                    <>
                        <RegularText fontSize={15} lineHeight={22} color="#4E5968" className="break-keep">
                            수강권을 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
                        </RegularText>
                        <Row
                            role="button"
                            tabIndex={0}
                            className="mt-[16px] self-start items-center justify-center h-[38px] px-[16px] rounded-[9px] bg-[#F2F4F6] cursor-pointer"
                            onClick={() => query.refetch()}
                            onKeyDown={(e) => e.key === 'Enter' && query.refetch()}
                        >
                            <RegularText fontSize={14} lineHeight={20} color="#3D4244">
                                다시 시도
                            </RegularText>
                        </Row>
                    </>
                ) : (
                    <Col className="gap-[8px] mt-[4px]">
                        <div className="w-full h-[18px] bg-[#F2F4F6] animate-pulse rounded-[4px]" />
                        <div className="w-2/3 h-[18px] bg-[#F2F4F6] animate-pulse rounded-[4px]" />
                    </Col>
                )}
            </Col>
        </Modal>
    );
};

export default SessionReportMembershipInfo;
